import React, { useEffect, useRef, useState } from 'react';
import NumberFormat from 'react-number-format';
import {
  Box,
  FormControlLabel,
  Grid,
  Link,
  makeStyles,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import { isNumber } from 'lodash';

import {
  amountToCurrencyString,
  formatPriceWithCents,
  formatAmountToCurrencyString,
} from '@utils/currency';
import {
  getCommerceStripePublicKeys,
  submitPaymentIntent,
} from '@api/backendAPI';
import { Button } from '@components/Form';
import { PaymentProcessingModal } from '@views/Payments/PaymentProcessingModal';
import { PaymentAmountConfirmationModal } from '@views/Payments/PaymentAmountConfirmationModal';
import { PaymentConfirmationModal } from '@views/Payments/PaymentConfirmationModal';
import { getLocalizedContent as l } from '@helpers/languages';
import content from './BankTransferPayment.json';
import { usePlaidOnSuccess } from '@hooks';
import { PlaidModalLoader } from '@views/Payments/PlaidModalLoader';
import paymentContent from './Payments.json';
import { isGreaterWithTolerance } from '@utils';

const useStyles = makeStyles(() => ({
  bankTransfer: {
    fontWeight: 500,
  },
  partialAmount: {
    width: '258px',
  },
  title: {
    fontFamily: 'SchnyderSLight',
    textAlign: 'center',
  },
  formHelperText: {
    margin: '0px !important',
  },
  bankTransferLabel: {
    color: 'var(--Grayscale-Nero, #1B1B1B)',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '16px',
    letterSpacing: '2.16px',
    textTransform: 'uppercase',
    marginBottom: '15px',
    fontStretch: 'condensed',
  },
  textField: {
    '& .MuiOutlinedInput-input': {
      display: 'flex',
      alignItems: 'flex-start',
      alignSelf: 'stretch',
      padding: '14px 36px',
    },
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        border: '1px solid var(--Grayscale-Fossil, #BEBBBB)',
      },
      '&.Mui-error fieldset': {
        border: '1px solid #FF0000',
      },
    },
    '& .MuiFormHelperText-contained': {
      marginLeft: '0px',
    },
  },
  howMuchToPayLabel: {
    color: 'var(--Grayscale-Nero, #1B1B1B)',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 300,
    lineHeight: '20px',
    letterSpacing: '-0.08px',
  },
  paymentOptionDescription: {
    color: 'var(--Grayscale-Nero, #1B1B1B)',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 300,
    lineHeight: '16px',
  },
  achDescription: {
    color: 'var(--Grayscale-Nero, #1B1B1B)',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 300,
    lineHeight: '16px',
    marginTop: '10px',
  },
  makePaymentButtonLabel: {
    textAlign: 'center',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '20px',
  },
  paySecurelyLabel: {
    color: 'var(--Nero, #1B1B1B)',
    fontSize: '22px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '130%',
    letterSpacing: '-0.44px',
  },
  backToPaymentOptionLabel: {
    color: ' var(--Grayscale-Nero, #1B1B1B)',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '20px',
    fontStretch: 'condensed',
  },
}));

export const BankTransferPayment = ({
  amountDue,
  onBackToPaymentOptionsClick,
  orderId,
  basepath,
  isOauth = false,
  availableReservationLimit = 0,
  orderFetchCompleted = false,
  hasPendingACHPayments,
}) => {
  const classes = useStyles();
  const [selectedAmount, setSelectedAmount] = useState({
    amount: 0,
    currency: amountDue?.currency || 'USD',
  });
  const [fullOrPartialPayment, setFullOrPartialPayment] = useState('partial');
  const [linkToken, setLinkToken] = useState('');
  const remainingAmountDue = amountDue;
  const amount = useRef(amountDue);
  const [displayAmountConfirmationModal, setDisplayAmountConfirmationModal] =
    useState(false);
  const [displayPaymentConfirmationModal, setDisplayPaymentConfirmationModal] =
    useState(false);
  const [displayProcessingModal, setDisplayProcessingModal] = useState(false);
  const [paymentError, setPaymentError] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const [paymentAmountError, setPaymentAmountError] = useState('');
  const { prefixLanguage } = useSelector(({ appState }) => appState);
  localStorage.removeItem('hasOauthRenderedOnce'); // Clean up Oauth flag
  const language = prefixLanguage || 'en';
  const searchParams = new URLSearchParams(window.location.search);
  const paymentProcessor =
    searchParams.get('processor') ||
    process.env.REACT_APP_STRIPE_PAYMENT_METHOD;

  const achDescription = l(content.achDescription, language).replace(
    'USD',
    `${formatAmountToCurrencyString(
      availableReservationLimit,
      selectedAmount.currency,
      language
    )}`
  );

  const onPlaidSuccess = usePlaidOnSuccess(
    isOauth,
    basepath,
    orderId,
    amount,
    setDisplayProcessingModal,
    setLinkToken,
    setDisplayAmountConfirmationModal,
    setPaymentError
  );

  const onCadPadSuccess = async (clientSecret, name, email) => {
    setDisplayProcessingModal(true);
    let stripeKey;
    try {
      stripeKey = await getCommerceStripePublicKeys('CAD');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      throw new Error('retrieving stripe public key');
    }

    const stripeInstance = await loadStripe(stripeKey);
    setDisplayProcessingModal(false);
    const { paymentIntent, error } =
      await stripeInstance.confirmAcssDebitPayment(clientSecret, {
        payment_method: {
          billing_details: {
            name,
            email,
          },
        },
      });
    setDisplayProcessingModal(true);
    setLinkToken('');
    if (error) {
      // eslint-disable-next-line no-console
      console.log(error);
      setPaymentError(true);
      setDisplayAmountConfirmationModal(true);
    } else {
      setDisplayAmountConfirmationModal(false);
      await submitPaymentIntent(orderId, paymentIntent.id);
      // window.location.assign(
      //   `${basepath}orders/${orderId}/payments/confirmation`
      // );
      setDisplayPaymentConfirmationModal(true);
    }
    setDisplayProcessingModal(false);
  };

  const onUsBankAccountLinkSuccess = async (clientSecret, name, email) => {
    setDisplayProcessingModal(true);
    let stripeKey;
    try {
      stripeKey = await getCommerceStripePublicKeys('USD');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      throw new Error('retrieving stripe public key');
    }

    const stripeInstance = await loadStripe(stripeKey);
    setDisplayProcessingModal(false);
    document.body.style.overflow = 'auto';
    if (
      process.env.REACT_APP_HOSTING_ENVIRONMENT === 'stage' ||
      process.env.REACT_APP_HOSTING_ENVIRONMENT === 'dev'
    ) {
      email = email.replace(
        email.split('@')[0],
        email.split('@')[0] + '+test_email'
      );
    }
    setDisplayAmountConfirmationModal(false);
    let { paymentIntent: paymentIntentResponse, error: stripeError } =
      await stripeInstance.collectBankAccountForPayment({
        clientSecret: clientSecret,
        params: {
          payment_method_type: 'us_bank_account',
          payment_method_data: {
            billing_details: {
              name,
              email,
            },
          },
        },
        expand: ['payment_method'],
      });
    if (stripeError) {
      console.log(JSON.stringify(stripeError));
      if (stripeError.type === 'canceled') {
        console.log('User canceled bank account collection');
      }
      setPaymentError(true);
      setDisplayAmountConfirmationModal(false);
      return;
    }
    if (paymentIntentResponse.status === 'requires_confirmation') {
      const { paymentIntent, error } =
        await stripeInstance.confirmUsBankAccountPayment(clientSecret);
      paymentIntentResponse = paymentIntent;
      stripeError = error;
      if (error) {
        setPaymentError(true);
        setDisplayAmountConfirmationModal(true);
      } else if (paymentIntent.status === 'requires_payment_method') {
        setPaymentError(true);
        setDisplayAmountConfirmationModal(true);
      } else if (
        paymentIntent.next_action?.type === 'verify_with_microdeposits'
      ) {
        localStorage.setItem(
          'microdepositVerificationUrl',
          paymentIntent.next_action?.verify_with_microdeposits
            ?.hosted_verification_url
        );
      }
      setDisplayProcessingModal(true);
      setLinkToken('');
      if (
        paymentIntentResponse.status !== 'requires_confirmation' &&
        paymentIntentResponse.status !== 'requires_source_action' &&
        paymentIntentResponse.status !== 'processing' &&
        paymentIntentResponse.status !== 'succeeded'
      ) {
        setDisplayAmountConfirmationModal(true);
      } else if (stripeError) {
        // eslint-disable-next-line no-console
        console.log(stripeError);
        setPaymentError(true);
        setDisplayAmountConfirmationModal(true);
      } else {
        setDisplayAmountConfirmationModal(false);
        const submitted = await submitPaymentIntent(
          orderId,
          paymentIntentResponse.id
        );
        setPaymentDetails(submitted);
        // window.location.assign(
        //   `${basepath}orders/${orderId}/payments/confirmation`
        // );
        setDisplayPaymentConfirmationModal(true);
      }
      setDisplayProcessingModal(false);
    }
  };

  useEffect(() => {
    // This keeps track of the selectedAmount since the onSuccess callback
    // loses it when passed to plaid
    amount.current = selectedAmount;
    handleMakePayment();
  }, [selectedAmount]);

  const formattedPrice = formatPriceWithCents(
    amountDue.amount,
    amountDue.currency
  );

  const disableMakePayment =
    orderFetchCompleted &&
    (selectedAmount.amount < 1 ||
      isGreaterWithTolerance(
        selectedAmount.amount,
        availableReservationLimit
      ) ||
      Number.isNaN(selectedAmount.amount));

  const handleMakePayment = () => {
    if (selectedAmount.amount < 1 && selectedAmount.amount !== 0) {
      setPaymentAmountError(l(content.amountTooLess, language));
      return;
    }

    if (
      isGreaterWithTolerance(selectedAmount.amount, availableReservationLimit)
    ) {
      setPaymentAmountError(
        l(content.amountLimit, language) +
          ' ' +
          formatAmountToCurrencyString(
            availableReservationLimit,
            selectedAmount.currency,
            language
          )
      );
      return;
    }

    setLinkToken('');
    setPaymentError(false);
  };

  const makePaymentButton = (
    <Button
      data-cy="data-make-payment-button"
      onClick={() => {
        setDisplayAmountConfirmationModal(true);
      }}
      disabled={disableMakePayment}
      hoverColor="#555354"
      text={l(content.makePayment, language)}
      disabledText={l(content.makePayment, language)}
      data-make-payment
      className={classes.makePaymentButtonLabel}
    />
  );

  const formatPlaceHolderText = language => {
    if (language === 'en-ca') {
      return `CAD$0`;
    } else if (language === 'fr-ca') {
      return `0 CAD`;
    }
    return 'Enter payment amount';
  };

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <Typography variant="h5" className={classes.paySecurelyLabel}>
          {l(paymentContent.paySecOnline, language)}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Link
          onClick={onBackToPaymentOptionsClick}
          data-back-to-payment-options
          className={classes.backToPaymentOptionLabel}
        >
          {l(content.backToPaymentOptions, language)}
        </Link>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h5" className={classes.howMuchToPayLabel}>
          {l(content.howMuchToPay, language)}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography
          variant="body2"
          className={classes.paymentOptionDescription}
        >
          {l(content.paymentOption, language)}
        </Typography>
        <Typography variant="h6" className={classes.achDescription}>
          {achDescription}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h6" className={classes.bankTransferLabel}>
          {l(content.payMethodPrompt, language)}
        </Typography>
        {/*<RadioGroup
          aria-label="fullOrPartialPayment"
          name="fullOrPartialPayment"
          value={fullOrPartialPayment}
          onChange={event => {
            setSelectedAmount(
              event.target.value === 'full'
                ? amountDue
                : {
                    amount: 0,
                    currency: amountDue.currency,
                  }
            );
            setFullOrPartialPayment(event.target.value);
          }}
        >
          <FormControlLabel
            value="full"
            control={<Radio />}
            label={l(content.fullPayment, language) + formattedPrice}
        />
          <FormControlLabel
            value="partial"
            control={<Radio />}
            label={l(content.partialPayment, language)}
            data-partial-payment
          />
        </RadioGroup>*/}
        {fullOrPartialPayment === 'partial' && (
          <NumberFormat
            format={value =>
              amountToCurrencyString(
                Number(value),
                amountDue.currency,
                language
              ).toString()
            }
            placeholder={formatPlaceHolderText(language)}
            customInput={props => (
              <TextField
                {...props}
                autoFocus={true}
                helperText={paymentAmountError}
                error={!!paymentAmountError}
                aria-describedby="component-error-text"
                FormHelperTextProps={{
                  classes: {
                    root: classes.formHelperText,
                  },
                }}
                className={classes.textField}
                inputProps={{
                  ...props.inputProps,
                  'aria-label': 'enter partial amount',
                  onKeyDown: e => {
                    if (
                      e.key === 'ArrowLeft' ||
                      e.key === 'ArrowRight' ||
                      e.key === 'Home' ||
                      e.key === 'End'
                    ) {
                      e.preventDefault();
                    }
                  },
                  onClick: e => {
                    const length = e.target.value.length;
                    e.target.setSelectionRange(length, length);
                  },
                  onSelect: e => {
                    const length = e.target.value.length;
                    if (e.target.selectionStart !== length) {
                      e.target.setSelectionRange(length, length);
                    }
                  },
                }}
              />
            )}
            inputProps={{ 'aria-label': 'enter partial amount' }}
            onValueChange={values => {
              setSelectedAmount({
                amount: values.floatValue / 100,
                currency: amountDue.currency,
              });
              setPaymentAmountError('');
            }}
            className={classes.partialAmount}
            data-partial-payment-amount
          />
        )}
      </Grid>
      <Grid item xs={12} container spacing={4}>
        {!isMobile && (
          <Grid item xs={6}>
            {makePaymentButton}
          </Grid>
        )}
      </Grid>

      {isMobile && (
        <Box width="100%">
          <Grid container>
            <Grid item xs={8} style={{ marginLeft: '12px' }}>
              {makePaymentButton}
            </Grid>
          </Grid>
        </Box>
      )}
      {!linkToken && displayAmountConfirmationModal && (
        <PaymentAmountConfirmationModal
          open={displayAmountConfirmationModal}
          onClose={() => setDisplayAmountConfirmationModal(false)}
          onTokenSuccess={setLinkToken}
          orderId={orderId}
          amountCharged={selectedAmount}
          paymentError={paymentError}
          onTryAgainError={() => {
            setPaymentError(false);
            setLinkToken('');
          }}
          hasPendingACHPayments={hasPendingACHPayments}
        />
      )}
      {displayPaymentConfirmationModal && (
        <PaymentConfirmationModal
          open={displayPaymentConfirmationModal}
          onClose={() =>
            window.location.assign(`${basepath}orders/${orderId}/payments`)
          }
          orderId={orderId}
          paymentDetails={{
            ...paymentDetails,
            amount: selectedAmount.amount * 100,
            currency: selectedAmount.currency,
            payment_method_type: 'account',
          }}
          paymentError={paymentError}
          onTryAgainError={() => {
            setPaymentError(false);
            setLinkToken('');
          }}
        />
      )}

      {linkToken &&
        (selectedAmount.currency === 'USD' ? (
          paymentProcessor === 'ach' ? (
            <CadStripeLoader
              onContinue={(accountHolderName, email) => {
                onUsBankAccountLinkSuccess(
                  linkToken.clientSecret,
                  accountHolderName,
                  email
                );
              }}
            />
          ) : (
            <PlaidModalLoader
              isOauth={isOauth}
              linkToken={linkToken}
              onPlaidSuccess={onPlaidSuccess}
              onPlaidReady={() => setDisplayAmountConfirmationModal(false)}
              onPlaidError={() => setPaymentError(true)}
              displayAmountConfirmationModal={displayAmountConfirmationModal}
              orderId={orderId}
              basepath={basepath}
            />
          )
        ) : (
          <CadStripeLoader
            onContinue={(accountHolderName, email) => {
              onCadPadSuccess(linkToken.clientSecret, accountHolderName, email);
            }}
            currency={selectedAmount.currency}
          />
        ))}

      <PaymentProcessingModal
        open={displayProcessingModal}
        onClose={() => setDisplayProcessingModal(false)}
        amountChargedString={formatPriceWithCents(
          selectedAmount.amount,
          selectedAmount.currency
        )}
      />
    </Grid>
  );
};

export const CadStripeLoader = ({ onContinue, currency }) => {
  // Accessing the Redux state
  const { firstName, lastName, email } = useSelector(
    state => state.userProfile.user
  );

  useEffect(() => {
    if (firstName && firstName.length > 0 && currency === 'CAD') {
      onContinue(`${firstName}${lastName}`, email);
    } else if (onContinue) onContinue(`${firstName}${lastName}`, email);
  }, []);

  return null;
};
